import { getApiUrl } from './api'
import { useAuthStore } from './auth'
import type { TrackWatchRequest } from '../../../shared/types/watch-history.types'

interface MovieData {
  tmdb_id: number
  title: string
  poster_path?: string | null
  backdrop_path?: string | null
  overview?: string | null
  release_date?: string | null
  vote_average?: number | null
  source_type: 'filestream' | 'gdrive' | 'aiostreams'
  source_id?: string | null
  stream_url?: string | null
  quality?: string | null
  telegram_file_id?: string | null
  gdrive_file_id?: string | null
}

class WatchTracker {
  private updateInterval: NodeJS.Timeout | null = null
  private lastPosition = 0
  private isPlaying = false
  private videoElement: HTMLVideoElement | null = null
  private movieData: MovieData | null = null
  
  startTracking(movieData: MovieData, videoElement: HTMLVideoElement) {
    this.movieData = movieData
    this.videoElement = videoElement
    
    console.log('🎬 WatchTracker: Starting tracking for', movieData.title)
    
    // Listen to video state changes (ONLY track when actually playing)
    videoElement.addEventListener('play', this.handlePlay)
    videoElement.addEventListener('playing', this.handlePlaying)
    videoElement.addEventListener('pause', this.handlePause)
    videoElement.addEventListener('ended', this.handleEnded)
    videoElement.addEventListener('waiting', this.handleWaiting)
    videoElement.addEventListener('seeking', this.handleSeeking)
    videoElement.addEventListener('seeked', this.handleSeeked)
    
    // Load saved position and resume
    this.loadSavedPosition()
    
    // Setup periodic tracking
    this.setupPeriodicTracking()
  }
  
  private async loadSavedPosition() {
    if (!this.movieData) return
    
    try {
      const headers = useAuthStore.getState().getAuthHeaders()
      const response = await fetch(getApiUrl('WATCH_HISTORY_RECENT'), {
        headers
      })
      const data = await response.json()
      
      if (data.success && data.data && Array.isArray(data.data)) {
        // Find the entry for this movie
        const entry = data.data.find((e: any) => 
          e.tmdb_id === this.movieData?.tmdb_id && 
          e.playback_position_seconds > 0
        )
        
        if (entry && this.videoElement) {
          const savedPosition = entry.playback_position_seconds
          this.videoElement.currentTime = savedPosition
          this.lastPosition = savedPosition
          console.log(`▶️ WatchTracker: Resuming from ${savedPosition}s`)
        }
      }
    } catch (error) {
      console.error('WatchTracker: Failed to load saved position:', error)
    }
  }
  
  private handlePlay = () => {
    console.log('🎬 WatchTracker: Video play event')
    this.isPlaying = true
  }
  
  private handlePlaying = () => {
    console.log('▶️ WatchTracker: Video playing (after buffering)')
    this.isPlaying = true
  }
  
  private handlePause = () => {
    console.log('⏸️ WatchTracker: Video paused')
    this.isPlaying = false
    // Save position immediately on pause
    this.saveCurrentPosition()
  }
  
  private handleEnded = () => {
    console.log('✅ WatchTracker: Video ended')
    this.isPlaying = false
    // Mark as completed
    this.markAsCompleted()
  }
  
  private handleWaiting = () => {
    console.log('⏳ WatchTracker: Video buffering')
    this.isPlaying = false
  }
  
  private handleSeeking = () => {
    this.isPlaying = false
  }
  
  private handleSeeked = () => {
    // Check if video is playing after seek
    if (this.videoElement && !this.videoElement.paused) {
      this.isPlaying = true
    }
  }
  
  private setupPeriodicTracking() {
    // Update every 30 seconds when video is playing
    console.log('⏰ WatchTracker: Setting up periodic tracking (every 30s)')
    
    // Clear any existing interval first
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
    }
    
    this.updateInterval = setInterval(() => {
      // Always check if we can update (will be validated inside updateProgress)
      // Only skip if video is definitely ended or doesn't exist
      if (!this.videoElement) {
        console.log('⏰ WatchTracker: Periodic update skipped - no video element')
        return
      }
      
      if (this.videoElement.ended) {
        console.log('⏰ WatchTracker: Periodic update skipped - video ended')
        return
      }
      
      // Always try to update unless video is ended
      // (updateProgress will validate duration and other requirements)
      console.log('⏰ WatchTracker: Periodic update triggered (30s interval)', {
        paused: this.videoElement.paused,
        currentTime: Math.floor(this.videoElement.currentTime),
        duration: Math.floor(this.videoElement.duration),
        readyState: this.videoElement.readyState
      })
      this.updateProgress()
    }, 30000) // 30 seconds
  }
  
  private async updateProgress() {
    if (!this.videoElement || !this.movieData) {
      console.warn('⚠️ WatchTracker: Cannot update progress - missing video element or movie data')
      return
    }
    
    const currentTime = this.videoElement.currentTime
    const duration = this.videoElement.duration
    
    // Don't track if duration is invalid
    if (isNaN(duration) || duration === 0) {
      console.warn('⚠️ WatchTracker: Cannot update progress - invalid duration', { duration, currentTime })
      return
    }
    
    try {
      console.log('📤 WatchTracker: Sending progress update to backend...', {
        currentTime: Math.floor(currentTime),
        duration: Math.floor(duration),
        percentage: Math.floor((currentTime / duration) * 100)
      })
      const trackData: TrackWatchRequest = {
        tmdb_id: this.movieData.tmdb_id,
        title: this.movieData.title,
        poster_path: this.movieData.poster_path,
        backdrop_path: this.movieData.backdrop_path,
        overview: this.movieData.overview,
        release_date: this.movieData.release_date,
        vote_average: this.movieData.vote_average,
        playback_position_seconds: Math.floor(currentTime),
        total_duration_seconds: Math.floor(duration),
        watch_duration_seconds: Math.floor(currentTime - this.lastPosition),
        source_type: this.movieData.source_type,
        source_id: this.movieData.source_id,
        stream_url: this.movieData.stream_url,
        quality: this.movieData.quality,
        telegram_file_id: this.movieData.telegram_file_id,
        gdrive_file_id: this.movieData.gdrive_file_id
      }
      
      const headers = { 
        'Content-Type': 'application/json',
        ...useAuthStore.getState().getAuthHeaders()
      }
      
      const response = await fetch(getApiUrl('WATCH_HISTORY_TRACK'), {
        method: 'POST',
        headers,
        body: JSON.stringify(trackData)
      })
      
      if (!response.ok) {
        console.error('❌ WatchTracker: Progress update failed:', response.status, response.statusText)
        const errorText = await response.text().catch(() => 'Unknown error')
        console.error('Error details:', errorText)
        return
      }
      
      const result = await response.json()
      this.lastPosition = currentTime
      const percentage = Math.floor((currentTime / duration) * 100)
      console.log(`✅ WatchTracker: Progress saved ${Math.floor(currentTime)}s / ${Math.floor(duration)}s (${percentage}%)`, result)
    } catch (error) {
      console.error('❌ WatchTracker: Failed to save progress:', error)
      if (error instanceof Error) {
        console.error('Error message:', error.message)
        console.error('Error stack:', error.stack)
      }
    }
  }
  
  private async saveCurrentPosition() {
    await this.updateProgress()
  }
  
  private async markAsCompleted() {
    if (!this.videoElement || !this.movieData) return
    
    const watchedPercentage = (this.videoElement.currentTime / this.videoElement.duration) * 100
    
    if (watchedPercentage >= 90) {
      try {
        const headers = {
          'Content-Type': 'application/json',
          ...useAuthStore.getState().getAuthHeaders()
        }
        
        await fetch(getApiUrl('WATCH_HISTORY_COMPLETE'), {
          method: 'POST',
          headers,
          body: JSON.stringify({
            tmdb_id: this.movieData.tmdb_id,
            completed: true
          })
        })
        console.log('✅ WatchTracker: Movie marked as completed (90%+ watched)')
      } catch (error) {
        console.error('WatchTracker: Failed to mark as completed:', error)
      }
    }
  }
  
  stopTracking() {
    console.log('⏹️ WatchTracker: Stopping tracking')
    
    // Save final position before stopping
    this.saveCurrentPosition()
    
    // Clear interval
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
      this.updateInterval = null
    }
    
    // Remove all event listeners
    if (this.videoElement) {
      this.videoElement.removeEventListener('play', this.handlePlay)
      this.videoElement.removeEventListener('playing', this.handlePlaying)
      this.videoElement.removeEventListener('pause', this.handlePause)
      this.videoElement.removeEventListener('ended', this.handleEnded)
      this.videoElement.removeEventListener('waiting', this.handleWaiting)
      this.videoElement.removeEventListener('seeking', this.handleSeeking)
      this.videoElement.removeEventListener('seeked', this.handleSeeked)
    }
    
    this.isPlaying = false
    this.videoElement = null
    this.movieData = null
  }
}

export const watchTracker = new WatchTracker()

